import { MKRequestQueryParams } from "../../alias";
import { v4 as uuidv4 } from "uuid";
import { createDecipheriv, createHash, createECDH, ECDH, Decipher } from "crypto";

/**
 * Utilities.
 * @internal
 */
export abstract class MKUtils {
    static ecdh: ECDH = createECDH("prime256v1"); // Equivalent to NID_X9_62_prime256v1

    /**
     * Get all URL query params.
     * @param url the input URL as string.
     * @param excludeKeys list of keys to exclude from the result. Note that keys are case sensitive.
     * @returns all the URL query params as an object.
     */
    public static getURLQueryParams(url: string, excludeKeys?: string[]): MKRequestQueryParams {
        const queryParamsObj: MKRequestQueryParams = {};
        try {
            if (!!url) {
                // Get the query string part from the URL
                let queryString = "";
                if (url.includes("?")) {
                    const urlParts = url.split("?");
                    if (urlParts.length > 0 && !!urlParts[1]) {
                        queryString = urlParts[1];
                    }
                } else if (url.includes("=")) {
                    queryString = url;
                }

                // process the query string to get the key-value pairs
                if (!!queryString) {
                    const queryParamsArr = queryString.split("&");
                    queryParamsArr.forEach((query: string) => {
                        const queryParts = query.split("=");
                        if (queryParts.length > 0) {
                            const queryKey = queryParts[0];
                            const queryValue = !!queryParts[1] ? queryParts[1] : "";
                            queryParamsObj[queryKey] = decodeURIComponent(queryValue);
                        } 
                    });
                }

                // exclude any params from the result that match with keys in the exclude list
                if (excludeKeys && excludeKeys.length > 0) {
                    excludeKeys.forEach((key) => {
                        delete queryParamsObj[key];
                    });
                }
            }
        } catch {
            // do nothing extra...
            console.warn("[MKUtils::getURLQueryParams] An error occurred during processing...!");
        }
        return queryParamsObj;
    }

    /**
     * Get value for the given query parameter in the URL.
     * @param url the input URL as string.
     * @param key the query parameter key as string.
     * @returns the value for the specified key if available.
     */
    public static getURLQueryParam(url: string, key: string): string | number | boolean | null | undefined {
        const queryParamsObj = MKUtils.getURLQueryParams(url);
        return queryParamsObj[key];
    }

    /**
     * Update or append the given params (key-value pairs) to the given URL as query parameters. 
     * @param url the URL to which the query parameters need to be appended.
     * @param params the object containing key-value pairs of query parameters that will be appended.
     * @returns the URL with the appended query parameters will be returned.
     */
    public static updateURLQueryParams(url: string, params?: MKRequestQueryParams): string {
        try {
            if (!!url && params) {
                // get merged query params object, overwriting any existing values for matching keys from given input params.
                const mergedQueryParamsObj = MKUtils.getURLQueryParams(url);
                for (const [key, value] of Object.entries(params)) {
                    if (value === null || value == undefined) {
                        mergedQueryParamsObj[key] = "";
                    } else {
                        mergedQueryParamsObj[key] = value;
                    }
                }

                // prepare the query string from the query params object.
                let queryString = "";
                const keyValueSeparator = "=";
                for (const [key, value] of Object.entries(mergedQueryParamsObj)) {
                    const querySeparator = !!!queryString ? "?" : "&";
                    queryString += querySeparator + encodeURIComponent(key) + keyValueSeparator + encodeURIComponent(value!.toString());
                }

                // update the given url
                if (url.includes("?")) {
                    const urlParts = url.split("?");
                    url = urlParts[0] + queryString;
                } else if (!!url) {
                    url += queryString;
                } else {
                    url = queryString.substring(1);
                }
            }
        } catch {
            console.warn("[MKUtils::updateURLQueryParams] An error occurred during processing...!");
        }
        return url;
    }

    /**
     * Encode to base64.
     * @param input input stringto encode to base64 string.
     * @returns encoded base64 string is returned.
     */
    public static base64Encode(input: string): string {
        try {
            return window.btoa(input);
        } catch {
            return "";
        }
    }

    /**
     * Decode from base64.
     * @param inputString encoded base64 string to decode from base64
     * @returns the decoded string.
     */
    public static base64Decode(inputString: string): string {
        try {
            return window.atob(inputString);
        } catch {
            return "";
        }
    }

    /**
     * Encode Uint8Array to base64 string.
     * @param input Uint8Array bytes.
     * @returns base64 encoded string.
     */
    public static base64EncodeUint8Array(input: Uint8Array): string {
        const keyStr: string = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/=";
        let output: string = "";
        let chr1: any, chr2: any, chr3: any, enc1: any, enc2: any, enc3: any, enc4: any;
        let i: number = 0;
        while (i < input.length) {
            chr1 = input[i++];
            chr2 = i < input.length ? input[i++] : Number.NaN; // Not sure if the index
            chr3 = i < input.length ? input[i++] : Number.NaN; // checks are needed here
    
            enc1 = chr1 >> 2;
            enc2 = ((chr1 & 3) << 4) | (chr2 >> 4);
            enc3 = ((chr2 & 15) << 2) | (chr3 >> 6);
            enc4 = chr3 & 63;
    
            if (isNaN(chr2)) {
                enc3 = enc4 = 64;
            } else if (isNaN(chr3)) {
                enc4 = 64;
            }
            output += keyStr.charAt(enc1) + keyStr.charAt(enc2) +
                keyStr.charAt(enc3) + keyStr.charAt(enc4);
        }
        return output;
    }

    /**
     * Decode to Uint8Array from base64 string.
     * @param input the encoded base64 string.
     * @returns the decoded Uint8Array.
     */
    public static base64DecodeUint8Array(input: string): Uint8Array {
        const raw: string = window.atob(input);
        const rawLength: number = raw ? raw.length : 0;
        if (rawLength) {
            let array: Uint8Array = new Uint8Array(new ArrayBuffer(rawLength));
            for (let i: number = 0; i < rawLength; i++)
                array[i] = raw.charCodeAt(i);
            return array;
        } else {
            return new Uint8Array();
        }
    }

    /**
     * Converts the Base64 to ArrayBuffer.
     * @param base64 the encoded base64 string.
     * @returns the arraybuffer.
     */
    public static base64ToArrayBuffer(base64: string):  ArrayBuffer {
        const binaryString: string = atob(base64);
        const length: number = binaryString.length;
        const bytes: Uint8Array = new Uint8Array(length);
        for (let i = 0; i < length; i++) {
          bytes[i] = binaryString.charCodeAt(i);
        }
        return bytes.buffer;
    }


    /**
     * Convert string to Uint8Array.
     * @param value input text string.
     * @returns Uint8Array for the input string.
     */
    public static stringToUint8Array(value: string): Uint8Array {
        return new TextEncoder().encode(value);
    }

    /**
     * Convert Uint8Array to string.
     * @param array input Uint8Array to be converted.
     * @returns a string for the given array.
     */
    public static stringFromUint8Array(array: Uint8Array): string {
        return new TextDecoder().decode(array);
    }

    /**
     * Get value for the given key in the object.
     * @param object the input object.
     * @param key key for which the value is needed.
     * @returns value for the given key or `undefined`.
     */
    public static pluckValue(object: {[key: string]: any}, key: string): any {
        return object[Object.keys(object).filter((k) => {
            return k.toLowerCase() === key.toLowerCase();
        })[0]];
    }

    /**
     * Generate and return an unique UUID string.
     * @returns a random unique UUID string is returned.
     */
    public static getUUID(): string {
        return uuidv4();
    }

    /**
     * Get current timestamp in milliseconds.
     * @returns current timestamp in milliseconds.
     */
    public static getCurrentTimestamp(): number {
        return Date.now();
    }

    /**
     * Get date-time as ISO string from timestamp in milliseconds.
     * @param timestamp timestamp in milliseconds.
     * @param skipMilliseconds when `true` will return ISO string without milliseconds.
     * @returns ISO date-time string.
     */
    public static timestampToISOString(timestamp: number, skipMilliseconds: boolean = true): string {
        if (skipMilliseconds) return new Date(timestamp).toISOString().split('.')[0] + "Z";
        return new Date(timestamp).toISOString();
    }

    /**
     * Get timestamp in milliseconds from ISO date-time string.
     * @param isoString date-time as ISO string.
     * @returns timestamp in milliseconds.
     */
    public static dateISOStringToTimestamp(isoString: string): number {
        const timestamp = new Date(isoString).getTime();
        if (isNaN(timestamp) || timestamp < 0) return 0;
        return timestamp;
    }

    /**
     * Get ISO date-time string by adding given number of seconds.
     * @param isoString date-time as ISO string.
     * @param seconds number of seconds to add to the given date-time.
     * @returns the new date-time ISO string after number of seconds are added to it.
     */
    public static dateISOStringByAddingSeconds(isoString: string, seconds: number): string {
        const milliseconds = seconds * 1000;
        let timestamp = new Date(isoString).getTime();
        if (isNaN(timestamp) || timestamp < 0) timestamp = 0;
        return new Date(timestamp + milliseconds).toISOString();
    }

    /**
     * Stringify the given object.
     * @param value any object to stringify.
     * @returns a valid string if successful or empty string in case of failure.
     */
    public static jsonStringify(value: object): string {
        try {
            return JSON.stringify(value);
        } catch {
            return "";
        }
    }

    /**
     * Converts a JavaScript Object Notation (JSON) string into an object.
     * @param text A valid JSON string.
     * @returns an object if the given text was a valid JSON string, else `null` is returned.
     */
    public static jsonParse(text: string): any {
        try {
            return JSON.parse(text);
        } catch {
            return null;
        }
    }

    /**
     * Check if the given value is a string or not.
     * @param value input value.
     * @returns `true` if input value is a string, `false` otherwise.
     */
    public static isString(value: any): boolean {
        return typeof(value) === "string";
    }

    /**
     * Check if the given value is a valid string or not.
     * @param value input value.
     * @returns `true` if the input value is a valid string, `false` otherwise.
     */
    public static isValidString(value: any): boolean {
        return (typeof(value) === "string" && !!value);
    }

    /**
     * Check if the given value is an object.
     * @param value input value.
     * @returns `true` if the input value is an object, `false` otherwise.
     */
    public static isObject(value: any): boolean {
        return (typeof(value) === "object");
    }

    /**
     * Check if the given value is a valid object or not.
     * @param value input value.
     * @returns `true` if input value is a valid object, `false` otherwise.
     */
    public static isValidObject(value: any): boolean {
        return (typeof(value) === "object" && Object.keys(value).length > 0);
    }

    /**
     * Check if the given value is an array or not.
     * @param value input value.
     * @returns `true` if input value is an array, `false` otherwise.
     */
    public static isArray(value: any): boolean {
        return (Array.isArray(value));
    }

    /**
     * Check if given value is a number or not.
     * @param value input value.
     * @returns `true` if input value is a number, `false` otherwise.
     */
    public static isNumber(value: any): boolean {
        return (typeof(value) === "number");
    }

    /**
     * Check if given value is a boolean or not.
     * @param value input value.
     * @returns `true` if input value is a boolean, `false` otherwise.
     */
    public static isBoolean(value: any): boolean {
        return (typeof(value) === "boolean");
    }

    /**
     * Check if the given value is an MKError object.
     * @param value input value.
     * @returns `true` if the input value is an MKError object, `false` otherwise.
     */
    public static isMKError(value: any): boolean {
        return (typeof(value) === "object" && "code" in value && "message" in value && "name" in value);
    }

    /**
     * Tests the given date string and returns if it's valid or not.
     * @param date an ISO date string in format `2023-06-27T10:00:00Z` or `2023-06-27T10:00:00.000Z`
     * @returns true if valid, false otherwise.
     */
    public static isValidISODateString(date: string): boolean {
        if (!!!date) return false;
        const pattern1 = /\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}Z/;            // 2023-06-27T10:00:00Z
        const pattern2 = /\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}.\d{3}Z/;      // 2023-06-27T10:00:00:000Z
        return pattern1.test(date.toLocaleUpperCase()) || pattern2.test(date.toLocaleUpperCase());
    }

    /**
     * Generates the ECDH key pair (Private and Public).
     * @returns privateKeyBase64 32bytes key, and publicKeyBase64 65 bytes (uncompressed) key.
     */
    public static generateECKeyPair(): {
        privateKeyBase64: string;
        publicKeyBase64: string;
    } {
        this.ecdh.generateKeys();
        const privateKeyBase64: string = this.ecdh.getPrivateKey().toString('base64'); // 32 bytes
        const publicKeyBase64: string = this.ecdh.getPublicKey().toString('base64');   // 65 bytes (uncompressed)
        return { privateKeyBase64, publicKeyBase64 };
    }

    /**
     * Get the shared key from client's private key and azuki's public key. 
     * @param base64PrivateKey client's private key.
     * @param base64PeerPublicKey azuki's public key.
     * @returns the Md5 Hash 128 bits base 64 encoded key.
     */
    public static deriveSharedSecretFromBase64(
        base64PrivateKey: string,
        base64PeerPublicKey: string
      ): string {
        
        const privateKey: any = Buffer.from(base64PrivateKey, 'base64');
        const peerPublicKey: any = Buffer.from(base64PeerPublicKey, 'base64');
        this.ecdh.setPrivateKey(privateKey);
        const sharedSecret: any = this.ecdh.computeSecret(peerPublicKey);
        
        // converts the shared key to Md5 hash base64 encoded 128bits.
        const md5Hash = createHash('md5').update(sharedSecret).digest('base64');
        return md5Hash; 
    }
    
    /**
     * AES Decryption using AES-128-CBC with a static IV.
     * @param encryptedText the chipertext (encrypted string).
     * @param tmpKey the key used to decrypt.
     * @returns the base 64 encoded decrypted string.
     */
     public static aesDecryption(encryptedText: string, tmpKey: string): string {
        const ivHex: string = '00000000000000000000000000000000'; // 16 bytes static IV
        const key: any = Buffer.from(tmpKey, 'base64');
        const iv: any = Buffer.from(ivHex, 'hex');
        const ciphertext: any = Buffer.from(encryptedText, 'base64');
        
        // decryption using CBC mode and Pkcs7 Padding.
        const decipher: Decipher = createDecipheriv('aes-128-cbc', key, iv);
        const decrypted: any = Buffer.concat([
            decipher.update(ciphertext),
            decipher.final()
        ]);
        return decrypted.toString('base64');
    }
}